import { useState } from "react";
import { motion } from "framer-motion";
import { Gift, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SPORTS = ["Calcio", "Basket", "Pallavolo", "Tennis", "Nuoto", "Atletica", "Rugby", "Altro"];

export const FreeSession = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", athlete_age: "", sport: "", challenge: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/free-sessions", { ...form, athlete_age: Number(form.athlete_age) });
      setDone(true);
      toast.success("Richiesta inviata! Ti ricontatteremo presto.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="prima-sessione" data-testid="free-session-section" className="py-16 sm:py-24 lg:py-32 bg-[#0A0D13] relative overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-50" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/10 border border-orange-500/30 text-orange-400">
            <Gift className="w-3.5 h-3.5" /> Offerta per nuovi atleti
          </span>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Prima Sessione <span className="text-orange-400">Gratuita</span>
          </h2>
          <p className="mt-5 text-base text-slate-300 leading-relaxed">
            Prima sessione gratuita senza impegno, durante la quale potrai chiedere al Mental Coach
            ciò che vuoi, fugando ogni dubbio su chi sono, cosa faccio e come lo faccio.
            La sessione avverrà attraverso una videochiamata su WhatsApp in modo da conoscerci
            sin dal primo istante. Insieme individuiamo i parametri da configurare e ti mostro e
            spiego come funziona il metodo. Se decidi di iniziare il percorso avrai:
          </p>
          <ul className="mt-6 space-y-3 text-sm text-slate-300">
            {["Analisi dello stato emotivo pre-gara", "Identificazione dei punti di forza mentali", "Mini-piano d'azione immediato"].map((t) => (
              <li key={t} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-sky-400 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}>
          {done ? (
            <div className="bg-[#10141D] border border-emerald-500/40 rounded-3xl p-10 text-center shadow-[0_0_40px_rgba(16,185,129,0.15)]">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
              <h3 className="mt-5 text-xl font-bold text-white font-heading">Richiesta ricevuta!</h3>
              <p data-testid="free-session-success-message" className="mt-3 text-sm text-slate-300">
                Ti contatterò entro 24-48 ore per fissare la tua prima sessione gratuita.
                Controlla la tua email per la conferma.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="free-session-form"
              className="bg-[#10141D]/90 backdrop-blur-xl border border-[#222A3B] rounded-3xl p-6 sm:p-8 space-y-5 shadow-2xl">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="fs-name" className="text-slate-300">Nome e cognome *</Label>
                  <Input id="fs-name" data-testid="free-session-name-input" required value={form.name} onChange={set("name")}
                    placeholder="Nome atleta o genitore" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
                <div>
                  <Label htmlFor="fs-age" className="text-slate-300">Età dell'atleta *</Label>
                  <Input id="fs-age" data-testid="free-session-age-input" type="number" min="6" max="99" required
                    value={form.athlete_age} onChange={set("athlete_age")} placeholder="14"
                    className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="fs-email" className="text-slate-300">Email *</Label>
                  <Input id="fs-email" data-testid="free-session-email-input" type="email" required value={form.email} onChange={set("email")}
                    placeholder="email@esempio.it" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
                <div>
                  <Label htmlFor="fs-phone" className="text-slate-300">Telefono *</Label>
                  <Input id="fs-phone" data-testid="free-session-phone-input" required value={form.phone} onChange={set("phone")}
                    placeholder="+39 333 1234567" className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Sport praticato *</Label>
                <Select required value={form.sport} onValueChange={set("sport")}>
                  <SelectTrigger data-testid="free-session-sport-select" className="mt-2 bg-[#08090C] border-[#222A3B] text-white">
                    <SelectValue placeholder="Seleziona lo sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#181E2B] border-[#222A3B] text-white">
                    {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fs-challenge" className="text-slate-300">La sfida principale *</Label>
                <Textarea id="fs-challenge" data-testid="free-session-challenge-input" required value={form.challenge} onChange={set("challenge")}
                  placeholder="Es. ansia pre-gara, calo di concentrazione, paura di sbagliare..."
                  className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500 min-h-[90px]" />
              </div>
              <Button type="submit" data-testid="free-session-submit-btn" disabled={loading}
                className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-12 text-base shadow-[0_0_25px_rgba(56,189,248,0.25)]">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Richiedi la Prima Sessione Gratuita"}
              </Button>
              <p className="text-xs text-slate-500 text-center">
                Inviando il modulo accetti la <a href="/privacy" className="text-sky-400 hover:underline">Privacy Policy</a>.
                Per atleti minorenni il modulo va compilato da un genitore o tutore.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
